import { useCallback } from 'react';
import shaka from 'shaka-player/dist/shaka-player.ui';

interface System73Config {
  apiKey?: string;
  contentSteeringEndpoint?: string;
  channelId?: string;
}

/**
 * Integration hook for System73 CDN optimization.
 * 
 * System73 provides intelligent CDN switching and content steering to optimize
 * video delivery based on real-time network conditions and CDN performance.
 * 
 * CRITICAL INTEGRATION REQUIREMENT:
 * --------------------------------
 * The System73 SDK MUST wrap the Shaka Player BEFORE the manifest is loaded.
 * This allows the SDK to intercept manifest parsing and detect all available
 * ABR (adaptive bitrate) profiles. If wrapped after loading, it only sees the
 * single bitrate Shaka initially selected, causing streams to report incorrectly
 * as mono-bitrate.
 * 
 * CORRECT SEQUENCE (handled by usePlayerInitialization):
 * 1. Initialize System73 wrapper: initializeSystem73(playerConfig)
 * 2. Initialize Shaka Player (skips auto-load): initializePlayer(video)
 * 3. Wrap the player: system73Wrapper.wrapPlayer(playerRef.current)
 * 4. Load manifest: loadManifest()
 * 
 * CONFIGURATION:
 * -------------
 * - apiKey: System73 API key for authentication
 * - contentSteeringEndpoint: Optional custom endpoint for content steering
 * - channelId: Optional identifier for analytics and monitoring
 * 
 * The SDK is loaded via CDN in useScriptLoader and attached to window.S73ShakaPlayerWrapper
 */
export const useSystem73 = (system73Config?: System73Config) => {
  const initializeSystem73 = useCallback((playerConfig: any) => {
    if (!system73Config?.apiKey || !window.S73ShakaPlayerWrapper) {
      return null;
    }

    try {
      const s73Config = {
        apiKey: system73Config.apiKey,
        contentSteeringEndpoint: system73Config.contentSteeringEndpoint,
        channelId: system73Config.channelId
      };

      const wrapper = window.S73ShakaPlayerWrapper(s73Config, { shaka });
      wrapper.wrapPlayerConfig(playerConfig);

      return wrapper;
    } catch (error) {
      console.error('System73 SDK initialization error:', error);
      return null;
    }
  }, [system73Config]);

  return { initializeSystem73 };
};
